import {
  FilePdfOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons"
import { graphql } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"
import useLightboxManager from "../../components/shared/hooks/useLightboxManager"
import { Block } from "../atoms/layout-primitives"
import { Text } from "../atoms/text"
import VariantContainer from "../atoms/variant-container"
import { MailLightbox as Lightbox } from "../organisms/lightbox"
import { BT } from "../styles/blog-typography"
import mergeVariants from "../util/merge-variants"
import NewsletterSignup from "./newsletter-signup"

export const ArticleFeatureFragment = graphql`
  fragment ArticleFeatureFragment on ContentfulArticleFeature {
    contentful_id
    __typename
    featureType
    icon
    iconColour {
      hex
    }
    heading
    copy
    url
    pdf {
      file {
        url
      }
    }
  }
`

/**
 * Returns the correct component based on the type of feature
 * @param type - The type of feature
 */
export const getArticleFeatureFromType = (type: string) => {
  switch (type.toLowerCase()) {
    case "link":
    case "pdf":
      return ArticleFeature.Link
    case "contact us":
      return ArticleFeature.Contact
    case "newsletter":
      return ArticleFeature.Newsletter
    default:
      // default has no interactivity
      return ArticleFeature
  }
}

// ArticleFeatures always link external so we use anchor not <Link/>
const FeatureLink = styled.a`
  text-decoration: none;
`

interface IArticleFeatureProps {
  icon?: "phone" | "mail" | "pdf" | "link"
  colour?: string
  title?: string
  body?: string
  link?: string
  variants?: any
}

/**
 * Permitted icon types.
 *
 * User selects from a list in the CMS.
 * */
const iconTypes: Record<IArticleFeatureProps["icon"], React.ReactNode> = {
  phone: <PhoneOutlined />,
  mail: <MailOutlined />,
  pdf: <FilePdfOutlined />,
  link: <LinkOutlined />,
}

/**
 * A feature card block for articles.
 * @param {string} icon - Name of icon to use
 * @param {string} colour - Colour of icon background
 * @param {string} title - Title of feature
 * @param {string} body - Body copy of feature
 */
const ArticleFeature = ({
  icon,
  colour,
  title,
  body,
  variants,
}: IArticleFeatureProps) => {
  return (
    <Block
      variants={[
        css`
          box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
          border-radius: 4px;
          margin: 0 auto;
          height: 100%;
          width: 100%;
          display: flex;
        `,
      ]}
    >
      <VariantContainer
        variants={mergeVariants(
          [
            css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 24px;
              gap: 16px;
              text-align: center;
            `,
            css`
              flex-direction: row;
              align-items: flex-start;
              text-align: left;
              padding: 32px;
              gap: 32px;
            `,
          ],
          variants
        )}
      >
        {/* ICON CONTAINER */}
        {icon && (
          <VariantContainer
            variants={[
              css`
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: ${colour || "var(--tricorn-black)"};
                border-radius: 4px;
                min-height: 64px;
                min-width: 64px;
                color: var(--white);
                font-size: 24px;
              `,
              css`
                min-height: 80px;
                min-width: 80px;
              `,
            ]}
          >
            {iconTypes[icon] || iconTypes.link}
          </VariantContainer>
        )}
        {/* BODY CONTAINER */}
        <VariantContainer
          variants={[
            css`
              display: flex;
              flex-direction: column;
              width: 100%;
              box-sizing: border-box;
              gap: 8px;
            `,
            css`
              gap: 16px;
            `,
          ]}
        >
          {title && (
            <Text
              as="h3"
              variants={[
                css`
                  ${BT.sm.h3`500`};
                `,
                css`
                  ${BT.lg.h3`500`}
                `,
              ]}
            >
              {title}
            </Text>
          )}
          {body && (
            <Text
              as="p"
              variants={[
                css`
                  ${BT.sm.body`400`};
                `,
                css`
                  ${BT.sm.body`400`};
                  font-size: 16px;
                `,
              ]}
            >
              {body}
            </Text>
          )}
        </VariantContainer>
      </VariantContainer>
    </Block>
  )
}

ArticleFeature.Link = ({ to, ...rest }) => (
  <FeatureLink href={to} target="_blank" rel="noreferrer">
    <ArticleFeature {...rest} />
  </FeatureLink>
)

ArticleFeature.Contact = ({ ...rest }) => {
  const contactPage = "https://www.mappedin.com/contact-us/" // external url to force new tab
  return (
    <FeatureLink href={contactPage} target="_blank" rel="noreferrer">
      <ArticleFeature {...rest} />
    </FeatureLink>
  )
}

ArticleFeature.Newsletter = ({ ...rest }) => {
  const [isLightboxVisible, toggleLightbox] = useLightboxManager()

  return (
    <>
      {isLightboxVisible && (
        <Lightbox toggle={toggleLightbox} index={0}>
          <VariantContainer
            variants={[
              css`
                background-color: var(--white);
                padding: 32px 16px;
                border-radius: 4px;
              `,
            ]}
          >
            <NewsletterSignup
              title="Subscribe to the Mappedin Newsletter"
              copy="Sign up for the latest industry updates, resources, and news."
            />
          </VariantContainer>
        </Lightbox>
      )}
      <VariantContainer
        //@ts-ignore
        onClick={toggleLightbox}
        variants={[
          css`
            cursor: pointer;
            background-color: var(--white);
            border: none;
          `,
        ]}
      >
        <ArticleFeature {...rest} />
      </VariantContainer>
    </>
  )
}

export default ArticleFeature
